<template>
  <section>
    <BasePageTitle
      :header="$label('user.signUp.title')"
      :subtitle="$label('user.signUp.subtitle')"
    />
    <div class="container">
      <div class="row d-flex justify-content-center">
        <div class="col-md-6 col-xl-5">
          <slot name="alert" />

          <LoginLocalProfile
            v-if="isAuthenticated && !isLoading"
            :is-loading="isLoading"
            :profile="loggedInUser"
            :on-submit="onSubmitWithAuth"
          />
        </div>
      </div>

      <div v-if="!isAuthenticated" class="row justify-content-center">
        <div class="col-md-6 col-lg-5 col-xl-4">
          <RegisterForm
            :on-submit="onSubmitWithEmail"
            :is-loading="isLoading"
            :login-path="loginPath"
            :is-b2b="false"
          />
          <p class="text-muted caption mt-md-16 pl-md-0 pr-md-0">
            {{ $label('user.signUp.privacyNote') }}
            <NuxtLink class="text-muted" to="/privacy">
              {{ $label('user.signUp.privacyPageLink') }}
            </NuxtLink>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useUserStore } from '~/stores/user'
import { useAccountStore } from '~/stores/account'
import BasePageTitle from '~/components/BasePageTitle.vue'
import RegisterForm from '~/components/register/RegisterForm'
import LoginLocalProfile from '~/components/LoginLocalProfile'
import { accountCreated } from '~/utils/trackingEvents'
import { BACKEND_ERRORS } from '~/utils/constants'
import { useAuthLib } from '~/composables/useAuthLib'

export default {
  components: {
    BasePageTitle,
    RegisterForm,
    LoginLocalProfile
  },
  props: {
    isB2b: {
      type: Boolean,
      default: false
    },
    nextPagePath: {
      type: String,
      required: true
    },
    loginPath: {
      type: String,
      default: ''
    }
  },
  setup() {
    const { signIn } = useAuthLib()
    const userStore = useUserStore()
    const accountStore = useAccountStore()

    return { signIn, userStore, accountStore }
  },
  data() {
    return {
      isLoading: false
    }
  },
  computed: {
    loggedInUser() {
      return this.userStore.loggedInUser
    },
    isAuthenticated() {
      return this.userStore.isAuthenticated
    }
  },
  methods: {
    nextStep() {
      this.$router.push({
        path: this.nextPagePath,
        query: this.$route.query
      })
    },
    onSubmitWithAuth() {
      this.nextStep()
    },
    async onSubmitWithEmail(data) {
      try {
        const { email, password } = data
        this.isLoading = true
        await this.accountStore.signUp(data)

        const currentPageQuery = new URLSearchParams(
          this.$route.query
        ).toString()
        const nextPage = `${this.nextPagePath}?${currentPageQuery}`
        await this.signIn({ email, password }, nextPage)

        this.sendTracking(email)
      } catch (error) {
        const errorLabel = this.$error.getLabel(error)
        const isValidationError = BACKEND_ERRORS.includes(errorLabel)
        const label = isValidationError
          ? `errors.${errorLabel}`
          : 'messages.errorOccuredMessage'

        this.$toast.error(this.$label(label))

        if (!isValidationError) {
          this.$sentryCaptureException(error)
        }
      } finally {
        this.isLoading = false
      }
    },
    sendTracking(email) {
      try {
        this.$gtmTracking.push({
          event: 'interact_event',
          eventAction: 'click',
          eventCategory: 'account created',
          eventLabel: `location: ${this.$route.fullPath}`
        })

        this.$activeCampaign.sendTrackingEvent(accountCreated(email))
        // eslint-disable-next-line no-empty
      } catch {}
    }
  }
}
</script>
